import {
  CartesianGrid,
  Line,
  LineChart,
  XAxis,
  YAxis
} from 'recharts'

import { useTranslation } from 'react-i18next'

import type { PriceHistory } from '@services/livability/types'

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@/components/ui/card'
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig
} from '@/components/ui/chart'

interface ApartmentPriceChartProps {
  priceHistory: PriceHistory[]
}

const AVAILABLE_COLORS = [
  '#11adbb',
  '#EF946C',
  '#8ed081',
  '#384d48',
  '#B57F50'
]

function assignSourceColors(sources: string[]): Record<string, string> {
  const colorMap: Record<string, string> = {}
  sources.forEach((source, index) => {
    colorMap[source] = AVAILABLE_COLORS[index % AVAILABLE_COLORS.length] ?? '#11adbb'
  })
  return colorMap
}

export function ApartmentPriceChart({ priceHistory }: ApartmentPriceChartProps) {
  const { t } = useTranslation('livability')

  if (!priceHistory || priceHistory.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('chart.title')}</CardTitle>
          <CardDescription>{t('chart.noData')}</CardDescription>
        </CardHeader>
      </Card>
    )
  }

  const sources = Array.from(new Set(priceHistory.map((h) => h.source_website)))
  const sourceColors = assignSourceColors(sources)

  const groupedByDate = priceHistory.reduce(
    (acc, record) => {
      const date = new Date(record.recorded_at)
      const monthYear = `${date.getMonth() + 1}/${date.getFullYear()}`

      if (!acc[monthYear]) {
        acc[monthYear] = { date: monthYear, timestamp: date.getTime() }
      }

      acc[monthYear][record.source_website] = record.price_month
      return acc
    },
    {} as Record<string, Record<string, unknown>>
  )

  const chartData = Object.values(groupedByDate).sort(
    (a, b) => (a.timestamp as number) - (b.timestamp as number)
  )

  const allPrices = priceHistory.map((h) => h.price_month)
  const minPrice = Math.min(...allPrices)
  const maxPrice = Math.max(...allPrices)
  const padding = (maxPrice - minPrice) * 0.1
  const yMin = Math.floor((minPrice - padding) / 100) * 100
  const yMax = Math.ceil((maxPrice + padding) / 100) * 100

  const chartConfig: ChartConfig = sources.reduce((config, source) => {
    config[source] = {
      label: source,
      color: sourceColors[source]
    }
    return config
  }, {} as ChartConfig)

  const monthNames = t('chart.months', { returnObjects: true }) as string[]
  const formatXAxis = (value: string) => {
    const [month, year] = value.split('/')
    if (!month || !year) return value
    return `${monthNames[parseInt(month) - 1]} '${year.slice(2)}`
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('chart.title')}</CardTitle>
        <CardDescription>{t('chart.description')}</CardDescription>
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig} className="h-[200px] w-full">
          <LineChart
            data={chartData}
            margin={{ left: 12, right: 12, top: 12, bottom: 12 }}
          >
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="date"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              tickFormatter={formatXAxis}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              tickFormatter={(value) => `${value}€`}
              domain={[yMin, yMax]}
            />
            <ChartTooltip content={<ChartTooltipContent valueSuffix="€" />} />
            {sources.map((source) => (
              <Line
                key={source}
                dataKey={source}
                type="monotone"
                stroke={sourceColors[source]}
                strokeWidth={2}
                dot={{ r: 3 }}
                connectNulls
              />
            ))}
          </LineChart>
        </ChartContainer>
      </CardContent>
    </Card>
  )
}
